import {
  useSelfTimeCapsules,
  useSharedTimeCapsules,
  useTimeCapsuleMembers,
  useUser
} from "api/ApiHooks"
import { PlusIcon } from "@heroicons/react/24/outline"
import dayjs from "dayjs"
import { Link, useParams } from "react-router-dom"
import { TimeCapsuleListItem } from "./TimeCapsuleListItem"

type SidebarCapsule = {
  id: string
  name: string
  slug?: string
  routePath: string
  opensAt?: string
  owned: boolean
  ownerName?: string
}

const isLocked = (capsule: SidebarCapsule) =>
  !!capsule.opensAt && dayjs(capsule.opensAt).isAfter(dayjs())

/** Owned item that also resolves whether the capsule has other members. */
const OwnedCapsuleItem = ({
  capsule,
  isSelected,
  onClick
}: {
  capsule: SidebarCapsule
  isSelected: boolean
  onClick?: () => void
}) => {
  const { data: membersData } = useTimeCapsuleMembers(capsule.id)
  const { data: userData } = useUser()
  const hasCollaborators = !!membersData?.members.some(
    (member) => member.id !== userData?.user.id
  )

  return (
    <TimeCapsuleListItem
      id={capsule.id}
      name={capsule.name}
      routePath={capsule.routePath}
      opensAt={capsule.opensAt}
      isSelected={isSelected}
      hasCollaborators={hasCollaborators}
      onClick={onClick}
    />
  )
}

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <h3 className="px-2 text-xs/6 font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
    {children}
  </h3>
)

export const TimeCapsuleSidebarList = ({ onClick }: { onClick?: () => void }) => {
  const ownQuery = useSelfTimeCapsules()
  const sharedQuery = useSharedTimeCapsules()
  const { slug } = useParams()

  const owned: SidebarCapsule[] = (ownQuery.data?.timeCapsules ?? []).map((capsule) => ({
    id: capsule.id,
    name: capsule.name,
    slug: capsule.slug,
    routePath: capsule.slug ?? "",
    opensAt: capsule.opensAt,
    owned: true
  }))

  const shared: SidebarCapsule[] = (sharedQuery.data?.timeCapsules ?? []).map(
    (capsule) => ({
      id: capsule.id,
      name: capsule.name,
      slug: capsule.slug,
      routePath: `shared/${capsule.creatorId}/${capsule.slug}`,
      opensAt: capsule.opensAt,
      owned: false,
      ownerName: capsule.creatorName
    })
  )

  const all = [...owned, ...shared]
  // Unopened first, soonest to open at the top.
  const upcoming = all
    .filter(isLocked)
    .sort((a, b) => dayjs(a.opensAt).valueOf() - dayjs(b.opensAt).valueOf())
  // Opened, most recently opened first.
  const opened = all
    .filter((capsule) => !isLocked(capsule))
    .sort((a, b) => dayjs(b.opensAt ?? 0).valueOf() - dayjs(a.opensAt ?? 0).valueOf())

  const isLoading = ownQuery.isLoading || sharedQuery.isLoading

  const renderItem = (capsule: SidebarCapsule) =>
    capsule.owned ? (
      <OwnedCapsuleItem
        key={capsule.id}
        capsule={capsule}
        isSelected={slug === capsule.slug}
        onClick={onClick}
      />
    ) : (
      <TimeCapsuleListItem
        key={capsule.id}
        id={capsule.id}
        name={capsule.name}
        routePath={capsule.routePath}
        opensAt={capsule.opensAt}
        ownerName={capsule.ownerName}
        isSelected={slug === capsule.slug}
        onClick={onClick}
      />
    )

  return (
    <div className="-mx-2 space-y-4">
      <section>
        <SectionHeader>Upcoming</SectionHeader>
        {isLoading ? (
          <p className="px-2 text-xs/7 font-semibold text-gray-400 dark:text-gray-500">
            Loading...
          </p>
        ) : upcoming.length === 0 ? (
          <p className="px-2 text-xs/7 text-gray-400 dark:text-gray-500">
            No upcoming capsules
          </p>
        ) : (
          <ul role="list" className="mt-1 space-y-1">
            {upcoming.map(renderItem)}
          </ul>
        )}
      </section>

      {opened.length > 0 && (
        <section className="border-t border-gray-200 pt-4 dark:border-gray-800">
          <SectionHeader>Opened</SectionHeader>
          <ul role="list" className="mt-1 space-y-1">
            {opened.map(renderItem)}
          </ul>
        </section>
      )}

      <Link
        to="/new/time-capsule"
        onClick={onClick}
        className="group flex items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-primary-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-primary-400"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center">
          <PlusIcon aria-hidden="true" className="h-5 w-5" />
        </span>
        Create new capsule
      </Link>
    </div>
  )
}
